/**
 * Enhanced Live API Hook
 * Integrates Google's direct approach with Flowcus features
 * @license Apache-2.0
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { 
  EnhancedGenAILiveClient
} from "../lib/enhanced-genai-live-client";
import { LiveClientOptions, StreamingLog } from "../types";
import { 
  LiveConnectConfig,
  LiveServerContent,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Modality
} from "@google/genai";

export interface UseEnhancedLiveAPIOptions {
  apiKey: string;
  model?: string;
  config?: Partial<LiveConnectConfig>;
}

export interface UseEnhancedLiveAPIReturn {
  client: EnhancedGenAILiveClient | null;
  connect: () => Promise<boolean>;
  disconnect: () => void;
  startRecording: () => Promise<boolean>;
  stopRecording: () => void;
  send: (parts: Part | Part[], turnComplete?: boolean) => void;
  
  // State
  connected: boolean;
  connecting: boolean;
  recording: boolean;
  
  // Audio contexts and nodes for visualization
  audioContexts: { input: AudioContext; output: AudioContext } | null;
  audioNodes: { input: AudioNode; output: AudioNode } | null;
  
  // Audio volume levels for visualization
  inputVolume: number;
  outputVolume: number;
  
  // Event data
  logs: StreamingLog[];
  content: LiveServerContent | null;
  interrupted: boolean;
  setupComplete: boolean;
  
  // Event handlers
  onSetupComplete: (handler: () => void) => void;
  onContent: (handler: (content: LiveServerContent) => void) => void;
  onToolCall: (handler: (toolCall: LiveServerToolCall) => void) => void;
  onToolCallCancellation: (handler: (cancellation: LiveServerToolCallCancellation) => void) => void;
  onInterrupted: (handler: () => void) => void;
  onLog: (handler: (log: StreamingLog) => void) => void;
}

export function useEnhancedLiveAPI(options: UseEnhancedLiveAPIOptions): UseEnhancedLiveAPIReturn {
  const clientRef = useRef<EnhancedGenAILiveClient | null>(null);
  
  // State
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioContexts, setAudioContexts] = useState<{ input: AudioContext; output: AudioContext } | null>(null);
  const [audioNodes, setAudioNodes] = useState<{ input: AudioNode; output: AudioNode } | null>(null);
  const [inputVolume, setInputVolume] = useState(0);
  const [outputVolume, setOutputVolume] = useState(0);
  const [logs, setLogs] = useState<StreamingLog[]>([]);
  const [content, setContent] = useState<LiveServerContent | null>(null);
  const [interrupted, setInterrupted] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  // Event handler refs
  const onSetupCompleteRef = useRef<(() => void) | null>(null);
  const onContentRef = useRef<((content: LiveServerContent) => void) | null>(null);
  const onToolCallRef = useRef<((toolCall: LiveServerToolCall) => void) | null>(null);
  const onToolCallCancellationRef = useRef<((cancellation: LiveServerToolCallCancellation) => void) | null>(null);
  const onInterruptedRef = useRef<(() => void) | null>(null);
  const onLogRef = useRef<((log: StreamingLog) => void) | null>(null);

  // Initialize client
  useEffect(() => {
    if (!options.apiKey) return;

    const clientOptions: LiveClientOptions = {
      apiKey: options.apiKey
    };

    const client = new EnhancedGenAILiveClient(clientOptions);
    clientRef.current = client;

    // Setup event listeners
    const handleOpen = () => {
      setConnected(true);
      setConnecting(false);
    };

    const handleClose = () => {
      setConnected(false);
      setConnecting(false);
      setRecording(false);
      setSetupComplete(false);
    };

    const handleError = (error: ErrorEvent) => {
      console.error("LiveAPI Error:", error);
      setConnecting(false);
      setConnected(false);
    };

    const handleSetupComplete = () => {
      setSetupComplete(true);
      onSetupCompleteRef.current?.();
    };

    const handleContent = (content: LiveServerContent) => {
      setContent(content);
      onContentRef.current?.(content);
    };

    const handleToolCall = (toolCall: LiveServerToolCall) => {
      onToolCallRef.current?.(toolCall);
    };

    const handleToolCallCancellation = (cancellation: LiveServerToolCallCancellation) => {
      onToolCallCancellationRef.current?.(cancellation);
    };

    const handleInterrupted = () => {
      setInterrupted(true);
      setTimeout(() => setInterrupted(false), 1000);
      onInterruptedRef.current?.();
    };

    const handleLog = (log: StreamingLog) => {
      setLogs(prev => [...prev.slice(-49), log]); // Keep last 50 logs
      onLogRef.current?.(log);
    };

    const handleAudioContexts = (contexts: { input: AudioContext; output: AudioContext }) => {
      setAudioContexts(contexts);
    };

    const handleAudioNodes = (nodes: { input: AudioNode; output: AudioNode }) => {
      setAudioNodes(nodes);
    };

    const handleInputAudio = (volume: number) => {
      setInputVolume(volume);
    };

    const handleOutputAudio = (volume: number) => {
      setOutputVolume(volume);
    };

    // Bind events
    client.on('open', handleOpen);
    client.on('close', handleClose);
    client.on('error', handleError);
    client.on('setupcomplete', handleSetupComplete);
    client.on('content', handleContent);
    client.on('toolcall', handleToolCall);
    client.on('toolcallcancellation', handleToolCallCancellation);
    client.on('interrupted', handleInterrupted);
    client.on('log', handleLog);
    client.on('audiocontexts', handleAudioContexts);
    client.on('audionodes', handleAudioNodes);
    client.on('inputaudio', handleInputAudio);
    client.on('outputaudio', handleOutputAudio);

    return () => {
      client.off('open', handleOpen);
      client.off('close', handleClose);
      client.off('error', handleError);
      client.off('setupcomplete', handleSetupComplete);
      client.off('content', handleContent);
      client.off('toolcall', handleToolCall);
      client.off('toolcallcancellation', handleToolCallCancellation);
      client.off('interrupted', handleInterrupted);
      client.off('log', handleLog);
      client.off('audiocontexts', handleAudioContexts);
      client.off('audionodes', handleAudioNodes);
      client.off('inputaudio', handleInputAudio);
      client.off('outputaudio', handleOutputAudio);
      
      client.disconnect();
      clientRef.current = null;
    };
  }, [options.apiKey]);

  // Connect function
  const connect = useCallback(async (): Promise<boolean> => {
    if (!clientRef.current || connecting || connected) return false;

    setConnecting(true);
    
    const defaultConfig: LiveConnectConfig = {
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: { voiceName: 'Orus' }
        }
      },
      ...options.config
    };

    try {
      const success = await clientRef.current.connect(
        options.model || 'gemini-2.5-flash-preview-native-audio-dialog',
        defaultConfig
      );
      
      if (!success) {
        setConnecting(false);
      }
      
      return success;
    } catch (error) {
      console.error("Connection failed:", error);
      setConnecting(false);
      return false;
    }
  }, [connecting, connected, options.config, options.model]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
  }, []);

  // Start recording function
  const startRecording = useCallback(async (): Promise<boolean> => {
    if (!clientRef.current || !connected || recording) return false;
    
    const success = await clientRef.current.startRecording();
    if (success) {
      setRecording(true);
    }
    return success;
  }, [connected, recording]);

  // Stop recording function
  const stopRecording = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.stopRecording();
      setRecording(false);
    }
  }, []);

  // Send function
  const send = useCallback((parts: Part | Part[], turnComplete: boolean = true) => {
    if (clientRef.current && connected) {
      clientRef.current.send(parts, turnComplete);
    }
  }, [connected]);

  // Event handler setters
  const onSetupComplete = useCallback((handler: () => void) => {
    onSetupCompleteRef.current = handler;
  }, []);

  const onContent = useCallback((handler: (content: LiveServerContent) => void) => {
    onContentRef.current = handler;
  }, []);

  const onToolCall = useCallback((handler: (toolCall: LiveServerToolCall) => void) => {
    onToolCallRef.current = handler;
  }, []);

  const onToolCallCancellation = useCallback((handler: (cancellation: LiveServerToolCallCancellation) => void) => {
    onToolCallCancellationRef.current = handler;
  }, []);

  const onInterrupted = useCallback((handler: () => void) => {
    onInterruptedRef.current = handler;
  }, []);

  const onLog = useCallback((handler: (log: StreamingLog) => void) => {
    onLogRef.current = handler;
  }, []);

  return {
    client: clientRef.current,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    send,
    
    // State
    connected,
    connecting,
    recording,
    audioContexts,
    audioNodes,
    inputVolume,
    outputVolume,
    logs,
    content,
    interrupted,
    setupComplete,
    
    // Event handlers
    onSetupComplete,
    onContent,
    onToolCall,
    onToolCallCancellation,
    onInterrupted,
    onLog
  };
}